import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import InterceptNavigationMessageModal from "@app/component/InterceptNavigationModal/components/InterceptNavigationMessageModal";
import BackButton from "@app/scene/map/components/sidebar/BackButton";
import BaseSidebar from "@app/scene/map/components/sidebar/Sidebar";
import Loader from "@app/scene/map/components/TerritoriesMode/components/Loader";
import Footer from "@app/scene/map/components/TerritoriesMode/components/Sidebar/Footer";
import Header from "@app/scene/map/components/TerritoriesMode/components/Sidebar/Header";
import { exitTerritoryMode } from "@app/scene/map/store/territoryMode/actions";
import { isUpdatingTerritory } from "@app/scene/map/store/territoryMode/selectors";
import DemographicData from "@app/scene/map/types/DemographicData";
import { RootState } from "@app/store/rootReducer";
import { deleteTerritory } from "@app/store/territories/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./BaseEditTerritorySidebar.module.scss";

interface Props {
  children: ReactNode;
  count?: number;
  extra?: ReactNode;
  fetchingShape?: boolean;
  loading?: boolean;
  onClose: () => void;
  onExitTerritoryMode: (forceExit?: true) => void;
  onFormChange?: (hasChanges: boolean) => void;
  onSave?: () => void;
  regionDemographyData?: Omit<DemographicData, "code" | "type">;
  regionsSelected?: number;
  saveDisabled?: boolean | ReactNode;
  shouldShowPrompt?: boolean;
  updating?: boolean;
}

const BaseEditTerritorySidebar: React.FC<Props> = ({
  children,
  count,
  extra,
  fetchingShape,
  loading,
  onClose,
  onExitTerritoryMode,
  onFormChange,
  onSave,
  regionDemographyData,
  regionsSelected,
  saveDisabled,
  shouldShowPrompt,
  updating,
}) => {
  const analytics = useAnalytics();
  useEffect(() => {
    analytics.initiated();
  }, [analytics]);

  const intl = useIntl();
  const [confirmExitModalVisible, setConfirmExitModalVisible] = useState(false);

  const handleClose = useCallback(async () => {
    if (shouldShowPrompt) {
      setConfirmExitModalVisible(true);
    } else {
      onFormChange?.(false);
      onExitTerritoryMode();
      analytics.cancelled("");
    }
  }, [analytics, onExitTerritoryMode, onFormChange, shouldShowPrompt]);

  const handleLeave = useCallback(() => onExitTerritoryMode(true), [onExitTerritoryMode]);

  return (
    <>
      <Loader
        loading={loading}
        regionDemographyData={regionDemographyData}
        regionsSelected={regionsSelected}
      />
      <BaseSidebar
        className={styles.container}
        onClose={handleClose}
        titleRaw={
          <div className={styles.title}>
            <BackButton
              className={styles.backButton}
              label={intl.formatMessage({
                id: "map.territories.sidebar.editBoundsDrawer.back",
                defaultMessage: "Back to Territory",
                description:
                  "Back button title in the Edit Territory Bounds drawer in Territory Mode",
              })}
              onClick={onClose}
            />
            <span>
              {intl.formatMessage({
                id: "map.territories.sidebar.editBoundsDrawer",
                defaultMessage: "Edit Bounds",
                description: "Title of the Edit Territory Bounds drawer in Territory Mode",
              })}
            </span>
          </div>
        }
        visible
      >
        <Header count={count} extra={extra} />
        <div className={styles.form}>{children}</div>
        <Footer
          loading={fetchingShape || updating}
          onCancel={handleClose}
          onSave={onSave}
          saveDisabled={fetchingShape || updating || saveDisabled || !shouldShowPrompt}
        />
        <InterceptNavigationModal
          loading={fetchingShape || updating}
          onLeave={handleLeave}
          onSave={onSave}
          shouldCloseOnLeave
          shouldShowPrompt={!!shouldShowPrompt}
        />
        <InterceptNavigationMessageModal
          analyticIssuer={analytics}
          loading={fetchingShape || updating}
          onLeave={handleLeave}
          onSave={onSave}
          onStay={() => setConfirmExitModalVisible(false)}
          subTitle={intl.formatMessage({
            id: "map.territories.sidebar.editDrawer.confirmLeave.message",
            defaultMessage:
              "If you leave before saving, your changes will be lost. Please save your updates before exiting.",
          })}
          visible={confirmExitModalVisible}
        />
      </BaseSidebar>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  updating: isUpdatingTerritory(state),
});

const mapDispatchToProps = {
  deleteTerritory: deleteTerritory.request,
  onExitTerritoryMode: exitTerritoryMode.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(BaseEditTerritorySidebar);
